/**
 * Dexie / IndexedDB data model for the Exemplu SRL demo.
 *
 * Primary keys are explicit strings we control (produse.id === cod, e.g. "P01";
 * clients "c1".., orders "cmd-1001"..) so seed cross-references and
 * `client.pretSpecial` (keyed by produsId) stay stable and human-readable.
 *
 * Screens read reactively via `useLiveQuery` (dexie-react-hooks); any write
 * through `db/queries.ts` re-renders every subscribed screen automatically.
 */

import Dexie, { type Table } from 'dexie'

// --- shared unions ---------------------------------------------------------
export type ClientTip = 'magazin' | 'minimarket' | 'carmangerie' | 'restaurant' | 'supermarket'

export type ComandaStatus = 'Nou' | 'Confirmat' | 'În pregătire' | 'Pe drum' | 'Livrat' | 'Facturat'

export type SoferStatus = 'disponibil' | 'pe traseu' | 'liber'
export type MasinaStatus = 'disponibilă' | 'pe traseu' | 'service'
export type UM = 'kg' | 'buc' | 'tavă'
export type EFacturaStatus = 'trimis' | 'validat' | 'în așteptare'
export type MesajStatus = 'nou' | 'transformat' | 'citit'

// --- row interfaces --------------------------------------------------------
export interface ComandaUzualaLinie {
  produsId: string
  cantitateKg: number
}

export interface Client {
  id: string
  nume: string
  tip: ClientTip
  localitate: string
  telefon: string
  adresa: string
  comandaUzuala: ComandaUzualaLinie[]
  /** per-product price override, keyed by produsId */
  pretSpecial: Record<string, number>
  soldRestant: number
  notite: string
  dataUltimaComanda: string
}

export interface Produs {
  id: string
  cod: string
  nume: string
  categorie: string
  pretKg: number
  stocKg: number
  abator: string
  um: UM
}

export interface ComandaLinie {
  produsId: string
  cod?: string
  nume?: string
  cantitateKg: number
  pretKg: number
  um?: UM
}

export interface TimelineEntry {
  status: ComandaStatus
  ora: string
}

export interface Comanda {
  id: string
  clientId: string
  dataComanda: string
  dataLivrare: string
  status: ComandaStatus
  linii: ComandaLinie[]
  totalKg: number
  totalRON: number
  soferId?: string
  masinaId?: string
  adresaLivrare: string
  mesajOriginal?: string
  codUIT?: string
  facturaId?: string
  timeline: TimelineEntry[]
}

export interface Sofer {
  id: string
  nume: string
  telefon: string
  permis: string
  masinaId?: string
  status: SoferStatus
}

export interface Masina {
  id: string
  nrInmatriculare: string
  capacitateKg: number
  frigorific: boolean
  status: MasinaStatus
  kmAzi: number
}

export interface FacturaParte {
  nume: string
  cui: string
  adresa: string
}

export interface FacturaLinie {
  nume: string
  cantitateKg: number
  pretKg: number
  um?: UM
}

export interface Factura {
  id: string
  comandaId: string
  serie: string
  numar: number
  dataEmitere: string
  furnizor: FacturaParte
  client: FacturaParte
  linii: FacturaLinie[]
  tva: number
  subtotal: number
  totalTVA: number
  total: number
  eFacturaStatus: EFacturaStatus
}

export interface Mesaj {
  id: string
  clientId?: string
  telefon: string
  numeAfisat: string
  text: string
  ora: string
  status: MesajStatus
  comandaId?: string
}

export interface Activitate {
  id: string
  tip: string
  text: string
  ora: string
  icon?: string
}

export interface Setari {
  id: number
  companie: string
  cui: string
  tagline: string
  anInfiintare: string
  adresa: string
  telefon: string
  iban: string
}

// --- database --------------------------------------------------------------
export class ExempluDB extends Dexie {
  clienti!: Table<Client, string>
  produse!: Table<Produs, string>
  comenzi!: Table<Comanda, string>
  soferi!: Table<Sofer, string>
  masini!: Table<Masina, string>
  facturi!: Table<Factura, string>
  mesaje!: Table<Mesaj, string>
  activitate!: Table<Activitate, string>
  setari!: Table<Setari, number>

  constructor() {
    super('exemplu-srl')
    this.version(1).stores({
      clienti: 'id, nume, localitate, tip',
      produse: 'id, cod, categorie',
      comenzi: 'id, clientId, status, dataLivrare',
      soferi: 'id, status',
      masini: 'id, status',
      facturi: 'id, comandaId, numar',
      mesaje: 'id, status, clientId',
      activitate: 'id, ora',
      setari: 'id',
    })
  }
}

export const db = new ExempluDB()

/** All Dexie tables, for bulk clear/reset. */
export const ALL_TABLES = [
  'clienti',
  'produse',
  'comenzi',
  'soferi',
  'masini',
  'facturi',
  'mesaje',
  'activitate',
  'setari',
] as const
